import React, { useEffect, useState } from "react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, LifeBuoy, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * iter71 — Modal de confirmação para envio em LOTE para Recuperação.
 *
 * Props:
 *   - items: [{record_type, record_id}, ...]  (já selecionados)
 *   - onClose(): fecha
 *   - onDone(summary): callback após sucesso (recarrega listas)
 */
export default function BatchRecoveryModal({ items, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/recovery/enter-batch", {
          items, reason: "preview dry_run", dry_run: true,
        });
        setPreview(data.preview);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Falha ao gerar prévia");
      } finally {
        setLoadingPreview(false);
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("Informe o motivo (mínimo 5 caracteres)");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/recovery/enter-batch", {
        items, reason: reason.trim(), dry_run: false,
      });
      setResult(data);
      toast.success(`${data.transferred_count} transferidos`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na operação");
    } finally { setSubmitting(false); }
  };

  const finish = () => {
    onDone && onDone(result);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="batch-recovery-modal">
      <div className="w-full max-w-2xl rounded-lg border border-[#262626] bg-[#0d0d0d] shadow-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <h3 className="font-display text-base flex items-center gap-2 text-amber-300">
            <LifeBuoy size={16} /> Enviar selecionados para Recuperação
          </h3>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="batch-recovery-close">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit}>
            <div className="px-5 py-4 space-y-4">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-8 text-[#737373]">
                  <Loader2 className="animate-spin mr-2" size={14}/> Carregando prévia…
                </div>
              ) : preview ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11.5px]">
                    <Cell label="Selecionados" value={preview.selected} testid="batch-selected"/>
                    <Cell label="Elegíveis" value={preview.eligible} accent="emerald" testid="batch-eligible"/>
                    <Cell label="Já em recuperação" value={preview.already_in_recovery} accent="amber" testid="batch-already"/>
                    <Cell label="Outros inelegíveis" value={preview.other_ineligible} accent="red" testid="batch-ineligible"/>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11.5px]">
                    <Cell label="Capital total" value={formatBRL(preview.capital_total)} accent="gold"/>
                    <Cell label="Saldo em aberto" value={formatBRL(preview.saldo_em_aberto_total)}/>
                    <Cell label="Juros pendentes" value={formatBRL(preview.juros_pendente_total)}/>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11.5px]">
                    <Cell label="Capital próprio" value={formatBRL(preview.capital_proprio_total)}/>
                    <Cell label="Capital de sócios" value={formatBRL(preview.capital_socios_total)}/>
                  </div>
                  {preview.distribuicao_por_socio?.length > 0 && (
                    <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] p-2">
                      <div className="text-[10px] uppercase tracking-wider text-[#737373] mb-1">
                        Distribuição por sócio
                      </div>
                      <div className="space-y-0.5 text-[11px] font-mono-tab max-h-32 overflow-auto">
                        {preview.distribuicao_por_socio.map((d) => (
                          <div key={d.socio_id} className="flex justify-between">
                            <span className="text-[#A3A3A3]">{d.socio_id.slice(0, 8)}…</span>
                            <span>{d.qtd} op. · {formatBRL(d.capital)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-200">
                    <AlertTriangle size={13} className="inline-block mr-1 -mt-0.5"/>
                    A transferência <strong>não</strong> altera o Capital na Rua total — apenas
                    reclassifica o saldo em aberto para a carteira de Recuperação. Nenhum
                    pagamento, comissão ou movimento financeiro será gerado.
                  </div>
                </>
              ) : null}

              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
                  Motivo (obrigatório, mín. 5 caracteres)
                </label>
                <textarea
                  rows={2} required minLength={5}
                  className="fc-input w-full text-[13px]"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex.: lote mensal de limpeza da carteira operacional"
                  data-testid="batch-reason-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]/50">
              <button type="button" onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
              <button
                type="submit"
                disabled={submitting || !preview || (preview?.eligible || 0) === 0}
                className="fc-btn-gold text-xs flex items-center gap-1.5"
                data-testid="batch-recovery-submit"
              >
                {submitting && <Loader2 size={12} className="animate-spin"/>}
                Confirmar envio para Recuperação
              </button>
            </div>
          </form>
        ) : (
          <div className="px-5 py-5 space-y-4">
            <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold">
              <CheckCircle2 size={16}/> Transferência concluída
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11.5px]">
              <Cell label="Selecionados" value={result.selected}/>
              <Cell label="Transferidos" value={result.transferred_count} accent="emerald" testid="result-transferred"/>
              <Cell label="Já em recuperação" value={result.skipped_count} accent="amber"/>
              <Cell label="Falhas" value={result.failed_count} accent={result.failed_count ? "red" : "neutral"}/>
            </div>
            <div className="grid grid-cols-1 gap-2 text-[11.5px]">
              <Cell label="Capital transferido" value={formatBRL(result.capital_transferred)} accent="gold"/>
            </div>
            {(result.skipped?.length > 0 || result.failed?.length > 0) && (
              <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] p-2 max-h-40 overflow-auto text-[11px]">
                {result.skipped?.map((s, i) => (
                  <div key={`sk-${i}`} className="flex justify-between py-0.5">
                    <span className="text-[#A3A3A3]">{s.record_id.slice(0, 8)}…</span>
                    <span className="text-amber-300">{s.reason}</span>
                  </div>
                ))}
                {result.failed?.map((f, i) => (
                  <div key={`fl-${i}`} className="flex justify-between py-0.5">
                    <span className="text-[#A3A3A3]">{f.record_id.slice(0, 8)}…</span>
                    <span className="text-red-300">{f.reason}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={finish} className="fc-btn-gold text-xs" data-testid="batch-result-close">
                Fechar e atualizar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, accent = "neutral", testid }) {
  const colors = {
    gold: "text-[#D4AF37]",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    red: "text-red-300",
    neutral: "text-white",
  };
  return (
    <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] px-2 py-1.5" data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-[12.5px] mt-0.5 ${colors[accent]}`}>{value}</div>
    </div>
  );
}
